import React, { useState } from "react";
import { Accordion, Switch, Text } from "@merge-api/merge-javascript-shared";

interface Props {
  enumFields: string[];
  selectedFields: string[];
  onChange: (selectedFields: string[]) => void;
}

const NORMALIZED = "normalized";
const ORIGINAL = "original";

const SWITCH_OPTIONS = [
  { value: NORMALIZED, label: "Normalized" },
  { value: ORIGINAL, label: "Original" },
];

const EnumOriginsSelect = ({ enumFields, selectedFields, onChange }: Props) => {
  // Frozen at mount so clearing a field does not remove its row mid-edit.
  const [initialFields] = useState<string[]>(() => [...enumFields, ...selectedFields]);
  const fields = Array.from(new Set([...initialFields, ...enumFields, ...selectedFields])).sort();
  const summary =
    selectedFields.length === 0
      ? "All normalized"
      : `${selectedFields.length} of ${fields.length} original`;

  const handleChange = (field: string) => (value: string) => {
    onChange(
      value === ORIGINAL
        ? Array.from(new Set([...selectedFields, field])).sort()
        : selectedFields.filter((selectedField) => selectedField !== field),
    );
  };

  return (
    <Accordion
      variant="none"
      chevronPlacement="left"
      chevronOrientation="right"
      className="px-4 pb-4"
      title={
        <div className="flex flex-row items-center gap-2">
          <Text variant="h6">Override with original values</Text>
          <Text variant="sm" className="text-gray-40">
            {summary}
          </Text>
        </div>
      }
    >
      <Text variant="sm" className="text-gray-40">
        Merge sends one value per enum field. Original replaces the normalized value.
      </Text>
      <div className="flex flex-col gap-3 mt-3">
        {fields.map((field) => (
          <div
            key={field}
            role="group"
            aria-label={field}
            className="flex flex-row items-center justify-between"
          >
            <Text variant="sm">{field}</Text>
            <Switch
              size="sm"
              options={SWITCH_OPTIONS}
              value={selectedFields.includes(field) ? ORIGINAL : NORMALIZED}
              onChange={handleChange(field)}
            />
          </div>
        ))}
      </div>
    </Accordion>
  );
};

export default EnumOriginsSelect;
